<template>
  <div
    v-if="variant === 'default'"
    class=" rounded-lg p-4"
    :class="[colors[color], classContainer]" role="alert" >
    <div class="flex flex-row items-center">
      <div v-if="icono" class="shrink-0" >
        <!-- Icon -->
        <span
          class="inline-flex justify-center items-center size-12 rounded-full border-4"
          :class="colorsIcon[color]">
          <slot name="icon">
            <component :is="iconos[icono]" class="shrink-0 size-6" ></component>
          </slot>
        </span>
        <!-- End Icon -->
      </div>
      <div class="ms-3">
        <h3 class="text-[16px] text-grey-800 font-semibold dark:text-white">
          {{titulo}}
        </h3>
        <div class="text-sm" :class="textColorClass">
          <p style="white-space: pre-line;">{{ texto }}</p>
        </div>
        <slot></slot>
      </div>
    </div>
  </div>
  <div
    v-else
    class="flex items-center gap-3 rounded-lg border px-3 py-2.5"
    :class="[minimalColors[color].container, classContainer]"
    role="alert"
  >
    <div
      v-if="icono"
      class="flex size-8 shrink-0 items-center justify-center rounded-full"
      :class="[minimalColors[color].icon, classIcon]"
    >
      <slot name="icon">
        <component :is="iconos[icono]" class="size-4" />
      </slot>
    </div>
    <div class="min-w-0">
      <p class="text-sm font-semibold" :class="minimalColors[color].title">
        {{ titulo }}
      </p>
      <p
        v-if="texto"
        class="whitespace-pre-line text-xs"
        :class="minimalColors[color].text"
      >
        {{ texto }}
      </p>
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as iconos from '@heroicons/vue/24/outline'
import { computed } from 'vue'
defineSlots<{
  default: () => unknown
  icon: () => unknown
}>();
type AlertColor = 'default' | 'gray' | 'graysecond' | 'blue' | 'green' | 'red' | 'yellow' | 'white' | 'orange' | 'purple' | 'teal' | 'sky' | 'indigo'

const props = withDefaults(defineProps<{
  classContainer?: string
  classIcon?: string
  titulo: string
  texto?: string
  color?: AlertColor
  icono?: keyof typeof iconos
  variant?: 'default' | 'minimal'
}>(), {
  classContainer: '',
  classIcon: '',
  texto: '',
  color: 'default',
  variant: 'default',
  icono: undefined
})
const { color, variant, classContainer, classIcon } = props
const colors = {
  default: 'bg-gray-100 border border-gray-400 text-sm text-gray-800 dark:bg-white/10 dark:border-white/20 dark:text-white',
  gray: 'bg-gray-100 border border-gray-400 text-sm text-gray-800 dark:bg-white/10 dark:border-white/20 dark:text-white',
  graysecond: 'bg-gray-50 border border-gray-400 text-sm text-gray-600 dark:bg-white/10 dark:border-white/10 dark:text-neutral-400',
  blue: 'bg-blue-100 border border-blue-400 text-sm text-blue-800 dark:bg-blue-900/10 dark:border-blue-800 dark:text-blue-500',
  green: 'bg-green-100 border border-green-400 text-sm text-green-800 dark:bg-green-900/10 dark:border-green-800 dark:text-green-500',
  red: 'bg-red-100 border border-red-400 text-sm text-red-800 dark:bg-red-900/10 dark:border-red-800 dark:text-red-500',
  yellow: 'bg-yellow-100 border border-yellow-400 text-sm text-yellow-800 dark:bg-yellow-900/10 dark:border-yellow-800 dark:text-yellow-500',
  white: 'bg-white/10 border border-white/10 text-sm text-white',
  orange: 'bg-orange-100 border border-orange-400 text-sm text-orange-800 dark:bg-orange-900/10 dark:border-orange-800 dark:text-orange-500',
  purple: 'bg-purple-100 border border-purple-400 text-sm text-purple-800 dark:bg-purple-900/10 dark:border-purple-800 dark:text-purple-500',
  teal: 'bg-teal-100 border border-teal-400 text-sm text-teal-800 dark:bg-teal-900/10 dark:border-teal-800 dark:text-teal-500',
  sky: 'bg-sky-100 border border-sky-400 text-sm text-sky-800 dark:bg-sky-900/10 dark:border-sky-800 dark:text-sky-500',
  indigo: 'bg-indigo-100 border border-indigo-400 text-sm text-indigo-800 dark:bg-indigo-900/10 dark:border-indigo-800 dark:text-indigo-500',
}

const colorsIcon = {
  default: 'border-blue-400 bg-blue-300 text-blue-700 dark:border-blue-900 dark:bg-blue-800 dark:text-blue-400',
  gray: 'bg-gray-100 border border-gray-400 text-sm text-gray-800 dark:bg-white/10 dark:border-white/20 dark:text-white',
  graysecond: 'bg-gray-100 border border-gray-400 text-sm text-gray-600 dark:bg-white/10 dark:border-white/10 dark:text-neutral-400',
  red: 'border-red-400 bg-red-300 text-red-700 dark:border-red-900 dark:bg-red-800 dark:text-red-400',
  blue: 'border-blue-400 bg-blue-300 text-blue-700 dark:border-blue-900 dark:bg-blue-800 dark:text-blue-400',
  green: 'border-green-400 bg-green-300 text-green-700 dark:border-green-900 dark:bg-green-800 dark:text-green-400',
  yellow: 'border-yellow-400 bg-yellow-300 text-yellow-700 dark:border-yellow-900 dark:bg-yellow-800 dark:text-yellow-400',
  white: 'border-white/20 bg-white/10 text-white',
  orange: 'border-orange-400 bg-orange-300 text-orange-700 dark:border-orange-900 dark:bg-orange-800 dark:text-orange-400',
  purple: 'border-purple-400 bg-purple-300 text-purple-700 dark:border-purple-900 dark:bg-purple-800 dark:text-purple-400',
  teal: 'border-teal-400 bg-teal-300 text-teal-700 dark:border-teal-900 dark:bg-teal-800 dark:text-teal-400',
  sky: 'border-sky-400 bg-sky-300 text-sky-700 dark:border-sky-900 dark:bg-sky-800 dark:text-sky-400',
  indigo: 'border-indigo-400 bg-indigo-300 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-800 dark:text-indigo-400',
}

const minimalColors: Record<AlertColor, {
  container: string
  icon: string
  title: string
  text: string
}> = {
  default: {
    container: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
    icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
    title: 'text-blue-900 dark:text-blue-100',
    text: 'text-blue-700 dark:text-blue-300'
  },
  gray: {
    container: 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900',
    icon: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    title: 'text-gray-900 dark:text-gray-100',
    text: 'text-gray-700 dark:text-gray-300'
  },
  graysecond: {
    container: 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900',
    icon: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    title: 'text-gray-800 dark:text-gray-100',
    text: 'text-gray-600 dark:text-gray-300'
  },
  blue: {
    container: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30',
    icon: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
    title: 'text-blue-900 dark:text-blue-100',
    text: 'text-blue-700 dark:text-blue-300'
  },
  green: {
    container: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30',
    icon: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
    title: 'text-green-900 dark:text-green-100',
    text: 'text-green-700 dark:text-green-300'
  },
  red: {
    container: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30',
    icon: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300',
    title: 'text-red-900 dark:text-red-100',
    text: 'text-red-700 dark:text-red-300'
  },
  yellow: {
    container: 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
    icon: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-300',
    title: 'text-amber-900 dark:text-amber-100',
    text: 'text-amber-700 dark:text-amber-300'
  },
  white: {
    container: 'border-white/30 bg-white/10',
    icon: 'bg-white/20 text-white',
    title: 'text-white',
    text: 'text-white/80'
  },
  orange: {
    container: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30',
    icon: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
    title: 'text-orange-900 dark:text-orange-100',
    text: 'text-orange-700 dark:text-orange-300'
  },
  purple: {
    container: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-950/30',
    icon: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300',
    title: 'text-purple-900 dark:text-purple-100',
    text: 'text-purple-700 dark:text-purple-300'
  },
  teal: {
    container: 'border-teal-200 bg-teal-50 dark:border-teal-800 dark:bg-teal-950/30',
    icon: 'bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-300',
    title: 'text-teal-900 dark:text-teal-100',
    text: 'text-teal-700 dark:text-teal-300'
  },
  sky: {
    container: 'border-sky-200 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/30',
    icon: 'bg-sky-100 text-sky-600 dark:bg-sky-900 dark:text-sky-300',
    title: 'text-sky-900 dark:text-sky-100',
    text: 'text-sky-700 dark:text-sky-300'
  },
  indigo: {
    container: 'border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950/30',
    icon: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-300',
    title: 'text-indigo-900 dark:text-indigo-100',
    text: 'text-indigo-700 dark:text-indigo-300'
  }
}

const textColorClass = computed(() => {
  switch (color) {
    case 'red':
      return 'text-red-700 dark:text-red-400'
    case 'blue':
      return 'text-blue-700 dark:text-blue-400'
    case 'green':
      return 'text-green-700 dark:text-green-400'
    case 'yellow':
      return 'text-yellow-700 dark:text-yellow-400'
    case 'orange':
      return 'text-orange-700 dark:text-orange-400'
    case 'purple':
      return 'text-purple-700 dark:text-purple-400'
    case 'teal':
      return 'text-teal-700 dark:text-teal-400'
    case 'sky':
      return 'text-sky-700 dark:text-sky-400'
    case 'indigo':
      return 'text-indigo-700 dark:text-indigo-400'
    case 'gray':
      return 'text-gray-700 dark:text-gray-400'
    default:
      return 'text-blue-700 dark:text-blue-400'
  }
})
</script>
